import type {
  CommentDiagramLocation,
  CommentMetadata,
  CommentShapeLocation,
  CommentState,
  CommentAuthorInfo,
} from './Comment';

export const COMMENT_AWARENESS_FIELD = 'commentEvents';
export const COMMENT_EVENTS_QUEUE_LIMIT = 20;
export const COMMENT_EVENT_SEEN_LIMIT = 200;

export type CommentRealtimeEventType =
  | 'comment:create'
  | 'comment:reply'
  | 'comment:update'
  | 'comment:resolve'
  | 'comment:reopen'
  | 'comment:delete';

/**
 * Lightweight representation of a comment node used for optimistic realtime hydration.
 * Replies are optional and limited to the immediate node that triggered the broadcast.
 */
export interface CommentNodeSnapshot {
  id: string;
  diagramId: string;
  threadRootId: string;
  parentId: string | null;
  authorId: string;
  author?: CommentAuthorInfo;
  body: string;
  state: CommentState;
  mentions?: string[];
  metadata: CommentMetadata;
  shapeId: string | null;
  locOnShape: CommentShapeLocation | null;
  locOnDiagram: CommentDiagramLocation | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  replies?: CommentNodeSnapshot[];
}

export interface CommentRealtimePayload {
  /**
   * Optional snapshot of the comment node involved in the event.
   * We limit the payload to the mutated node to keep broadcasts lightweight.
   */
  comment?: CommentNodeSnapshot;
  /**
   * Additional context (e.g. metadata patches) if needed.
   * Keep the object shallow to avoid bloating awareness state.
   */
  extras?: Record<string, unknown>;
}

export interface CommentRealtimeEvent {
  id: string;
  type: CommentRealtimeEventType;
  diagramId: string;
  threadRootId: string;
  commentId: string;
  authorId?: string;
  /**
   * Awareness client ID of the publisher. Useful for echo suppression.
   */
  originClientId?: number;
  timestamp: number;
  payload?: CommentRealtimePayload;
}

export function trimCommentEventQueue(
  events: CommentRealtimeEvent[],
  limit: number = COMMENT_EVENTS_QUEUE_LIMIT,
): CommentRealtimeEvent[] {
  if (events.length <= limit) {
    return events;
  }
  return events.slice(-limit);
}
